import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Notification as NotificationModel, SyncEvent, MarketingEmailSendStatusEvent } from '../api/generated/model';
import { useNotificationStream, ConnectionStatus } from '../hooks/useNotificationStream';
import { useSyncEvents } from '../hooks/useSyncEvents';
import { useMarketingEmailSendEvents } from '../hooks/useMarketingEmailSendEvents';
import { usePutNotificationsIdRead, usePutNotificationsIdArchive, useDeleteNotificationsId, getNotifications } from '../api/generated/arrayscapeGamingDashboardAPI';
import { useAuthUser } from '../hooks/useAuthUser';

// Polling interval for notifications (in milliseconds)
const NOTIFICATION_POLL_INTERVAL = 30000; // 30 seconds
const NOTIFICATIONS_PAGE_SIZE = 20;

interface NotificationContextValue {
  notifications: NotificationModel[];
  unreadCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  error: Error | null;
  isDismissed: boolean;
  toggleRead: (notificationId: string) => Promise<void>;
  archiveNotification: (notificationId: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  loadMore: () => Promise<void>;
  clearAll: () => void;
  refresh: () => void;
  reconnect: () => void;
  dismissError: () => void;
  // Sync events
  activeSyncs: Map<string, SyncEvent>;
  getSyncState: (syncId: string) => SyncEvent | undefined;
  isSyncing: (syncId?: string) => boolean;
  // Marketing email send events
  activeMarketingEmailSends: Map<string, MarketingEmailSendStatusEvent>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [errorDismissed, setErrorDismissed] = useState(false);
  const [errorDisplayTime, setErrorDisplayTime] = useState<number | null>(null);
  const [showError, setShowError] = useState(false);

  // Notification state
  const [allNotifications, setAllNotifications] = useState<NotificationModel[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if user is authenticated (has token)
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('token');
  });

  // Listen for token changes (login/logout)
  useEffect(() => {
    const checkAuth = () => {
      const hasToken = !!localStorage.getItem('token');
      setIsAuthenticated(hasToken);
    };

    // Check on mount
    checkAuth();

    // Listen for storage events (from other tabs)
    window.addEventListener('storage', checkAuth);

    // Check periodically (for same-tab changes)
    const interval = setInterval(checkAuth, 1000);

    return () => {
      window.removeEventListener('storage', checkAuth);
      clearInterval(interval);
    };
  }, []);

  // Get current user to detect impersonation changes
  const { data: currentUser } = useAuthUser();
  const previousUserIdRef = useRef<string | undefined>(undefined);

  // Mutations
  const markAsReadMutation = usePutNotificationsIdRead();
  const archiveMutation = usePutNotificationsIdArchive();
  const deleteMutation = useDeleteNotificationsId();

  // Fetch notifications (initial or with cursor for pagination)
  const fetchNotifications = useCallback(async (cursor?: string | null, append = false) => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const result = await getNotifications(cursor
        ? { cursor, limit: NOTIFICATIONS_PAGE_SIZE }
        : { sort_field: 'created_at', sort_direction: 'desc', limit: NOTIFICATIONS_PAGE_SIZE },
      );

      const newNotifications = result.data ?? [];
      const newUnreadCount = result.unread_count ?? 0;
      const newCursor = result.pagination?.cursor ?? null;

      if (append) {
        // Append to existing notifications (for load more)
        setAllNotifications(prev => {
          // Filter out duplicates
          const existingIds = new Set(prev.map(n => n.id));
          const uniqueNew = newNotifications.filter(n => !existingIds.has(n.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        // Replace notifications (for initial load or refresh)
        setAllNotifications(newNotifications);
      }

      setUnreadCount(newUnreadCount);
      setNextCursor(newCursor);
      setHasMore(!!newCursor);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initial load and polling
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();

      // Set up polling
      const interval = setInterval(() => {
        fetchNotifications();
      }, NOTIFICATION_POLL_INTERVAL);

      return () => clearInterval(interval);
    } else {
      // Clear notifications when not authenticated
      setAllNotifications([]);
      setUnreadCount(0);
      setNextCursor(null);
      setHasMore(false);
    }
  }, [isAuthenticated, fetchNotifications]);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (nextCursor && !isLoading) {
      await fetchNotifications(nextCursor, true);
    }
  }, [nextCursor, isLoading, fetchNotifications]);

  // Sync events hook
  const {
    activeSyncs,
    handleSyncEvent,
    getSyncState,
    isSyncing,
  } = useSyncEvents();

  // Marketing email send events hook (parallel to useSyncEvents)
  const {
    activeMarketingEmailSends,
    handleMarketingEmailSendEvent,
  } = useMarketingEmailSendEvents();

  // SSE hook for sync events only (notifications are polled via REST)
  const {
    isConnected,
    connectionStatus,
    error,
    reconnect,
  } = useNotificationStream({
    enabled: isAuthenticated,
    onSyncEvent: (syncEvent) => {
      // Forward sync events to the sync events handler
      handleSyncEvent(syncEvent);
    },
    onMarketingEmailSendEvent: (sendEvent) => {
      handleMarketingEmailSendEvent(sendEvent);
    },
  });

  // Detect user changes (impersonation start/stop) and refetch
  useEffect(() => {
    const currentUserId = currentUser?.id;
    if (previousUserIdRef.current !== undefined && previousUserIdRef.current !== currentUserId) {
      // User changed - refetch notifications and reconnect SSE for sync events
      fetchNotifications();
      reconnect();
    }
    previousUserIdRef.current = currentUserId;
  }, [currentUser?.id, reconnect, fetchNotifications]);

  // Toggle notification read state
  const toggleRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsReadMutation.mutateAsync({ id: notificationId });
        // Refetch to get updated list and count
        fetchNotifications();
      } catch (err) {
        console.error('Failed to toggle notification read state:', err);
        throw err;
      }
    },
    [markAsReadMutation, fetchNotifications]
  );

  // Archive notification
  const archiveNotification = useCallback(
    async (notificationId: string) => {
      try {
        await archiveMutation.mutateAsync({ id: notificationId });
        // Refetch to get updated list and count
        fetchNotifications();
      } catch (err) {
        console.error('Failed to archive notification:', err);
        throw err;
      }
    },
    [archiveMutation, fetchNotifications]
  );

  // Delete notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        await deleteMutation.mutateAsync({ id: notificationId });
        // Refetch to get updated list and count
        fetchNotifications();
      } catch (err) {
        console.error('Failed to delete notification:', err);
        throw err;
      }
    },
    [deleteMutation, fetchNotifications]
  );

  // Clear all notifications (just refetch - no local state to clear)
  const clearAll = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refresh notifications from REST API
  const refresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Dismiss error banner
  const dismissError = useCallback(() => {
    setErrorDismissed(true);
  }, []);

  // Manage error display with stability logic to prevent banner flashing
  // Note: error is guaranteed to be null when connected (via reducer)
  useEffect(() => {
    if (error) {
      // Track when error first occurred
      if (errorDisplayTime === null) {
        setErrorDisplayTime(Date.now());

        // Only show error if it persists for more than 2 seconds
        // Create timer only once when error first occurs
        const timer = setTimeout(() => {
          setShowError(true);
        }, 2000);

        return () => clearTimeout(timer);
      }
      // If errorDisplayTime already exists, we're already tracking this error
      // Don't create a new timer, just keep showing the error if showError is true
    } else {
      // Connection restored or no error - clear error display
      setShowError(false);

      if (errorDisplayTime !== null) {
        setErrorDisplayTime(null);
      }
    }
  }, [error, errorDisplayTime]);

  const value: NotificationContextValue = {
    notifications: allNotifications,
    unreadCount,
    hasMore,
    isLoading,
    isConnected,
    connectionStatus,
    error: showError ? error : null, // Only expose error if it should be shown
    isDismissed: errorDismissed,
    toggleRead,
    archiveNotification,
    deleteNotification,
    loadMore,
    clearAll,
    refresh,
    reconnect,
    dismissError,
    // Sync events
    activeSyncs,
    getSyncState,
    isSyncing,
    // Marketing email send events
    activeMarketingEmailSends,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
